import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  useNodesState, 
  useEdgesState, 
  Node, 
  Edge, 
  Panel, 
  useReactFlow,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTheme } from '../../contexts/ThemeContext';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { useWorkflowNotifications } from '../../utils/notifications';
import { logWorkflowEvent } from './WorkflowEventLog';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { WorkflowContextMenuManager } from './WorkflowContextMenuManager';
import { UnifiedContextMenu } from '../ui/UnifiedContextMenu';
import { BaseGraphCanvas } from '../graphs/BaseGraphCanvas';
import { workflowNodeTypes } from './nodes';
import { workflowEdgeTypes } from './edges';
import { TemplateSelector, WorkflowTemplates } from './WorkflowTemplates';
import { ChatWithCanvas } from './ChatWithCanvas';
import { FiMessageSquare } from 'react-icons/fi';

// Import extracted utilities and hooks
import { useWorkflowOperations } from './hooks/useWorkflowOperations';
import { useWorkflowContextMenu } from './hooks/useWorkflowContextMenu';
import { useWorkflowHistory } from './hooks/useWorkflowHistory';
import { usePostMutationLayout } from './hooks/usePostMutationLayout';
import { useLayoutHistory } from './hooks/useLayoutHistory';
import { LayoutRefreshButton } from './LayoutRefreshButton';
import { processWorkflowData, updateNodesWithWorkflowData, validateAndNormalizePosition } from './utils/workflowDataProcessor';
import * as workflowDataProcessor from './utils/workflowDataProcessor';
import { createEdgesFromConnections } from './utils/workflowNodeEdgeUtils';
import { applyLayout, calculateCriticalPath } from './utils/workflowLayout';
import { validateWorkflow } from './utils/workflowValidation';
import { WorkflowMetricsPanel } from './WorkflowMetricsPanel';
import { ValidationPanel } from './ValidationPanel';
import { WorkflowToolbar } from './WorkflowToolbar';
import * as sessionService from '../../services/sessionService';
import * as workflowExecutionService from '../../services/workflowExecutionService';
import { useNavigate } from 'react-router-dom';

// Import position debugging utilities
import { logNodePosition, validatePosition, logAllNodePositions, checkPositionPreservation } from '../../utils/positionDebugUtils';

// Import types from the shared types file
import { WorkflowCanvasMode } from './types';

// Global debug callback for recovery scenarios
declare global {
  interface Window {
    workflowDebugCallback?: (type: string, nodeId: string, updates: any) => void;
  }
}

interface WorkflowCanvasProps {
  workflowData: any;
  mode: WorkflowCanvasMode;
  onWorkflowChange?: (change: any) => void;
  executionData?: any;
  height?: string;
  readOnly?: boolean;
  onSave?: (workflow: any) => void;
  fullScreen?: boolean; // New prop for full screen mode
}

// Phase types for the canvas context menu
const phaseTypes = [
  { id: 'team_selection', label: 'Team Selection' },
  { id: 'brainstorming', label: 'Brainstorming' },
  { id: 'critique', label: 'Critique' },
  { id: 'synthesis', label: 'Synthesis' },
  { id: 'refinement', label: 'Refinement' }
];

// Wrapper component to ensure ReactFlowProvider is always present
export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = (props) => {
  // We need to wrap the content with ReactFlowProvider
  return (
    <ReactFlowProvider>
      <WorkflowCanvasContent {...props} />
    </ReactFlowProvider>
  );
};


// Main component content
const WorkflowCanvasContent: React.FC<WorkflowCanvasProps> = ({
  workflowData,
  mode,
  onWorkflowChange,
  executionData,
  height = '600px',
  readOnly = false,
  onSave,
  fullScreen = false
}) => {
  // Calculate dynamic height for edit mode
  const getCanvasHeight = () => {
    if (fullScreen) {
      return 'calc(100vh - 120px)'; // Account for toolbar and padding
    }
    if (mode === WorkflowCanvasMode.EDIT) {
      return 'calc(100vh - 180px)'; // Reduced height for horizontal toolbar
    }
    return height;
  };
  const { darkMode } = useTheme();
  const { fitView } = useReactFlow();
  const reactFlowWrapper = useRef(null);
  const workflowNotifications = useWorkflowNotifications();
  const navigate = useNavigate();
  
  // Test workflow modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [availableSessions, setAvailableSessions] = useState<any[]>([]);

  // Chat state (COPIED FROM PersonaForge pattern)
  const [showChat, setShowChat] = useState(false);
  const [pendingChatMessage, setPendingChatMessage] = useState<string>('');

  // Auto-open chat if ?chat=open is in the URL (e.g., when navigating from Send-To)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('chat') === 'open') {
      setShowChat(true);
      // Clean up the URL param
      const url = new URL(window.location.href);
      url.searchParams.delete('chat');
      url.searchParams.delete('seeded');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // State for nodes and edges — MUST be declared before any hook that references them
  const [nodes, setNodes, onNodesChangeOriginal] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Undo/Redo system - MUST be declared before onNodesChange
  const { pushState, undo, redo, canUndo, canRedo, currentState } = useWorkflowHistory();

  // Layout undo history — single-purpose, separate from workflow undo/redo
  const { pushLayoutSnapshot, undoLayout, canUndoLayout } = useLayoutHistory(setNodes);

  // Post-mutation layout — fires after chat-with-canvas mutations land in state
  const { triggerLayout, relayout, isLayoutPending } = usePostMutationLayout(
    nodes,
    edges,
    setNodes,
    fitView,
    { direction: 'TB', fitViewDuration: 800, onBeforeLayout: pushLayoutSnapshot }
  );

  // Handle workflow updates from chat with comprehensive logging
  const handleWorkflowUpdate = useCallback((updatedWorkflow: any) => {
    console.log('🔄 WORKFLOW-DEBUG: handleWorkflowUpdate called with:', {
      updatedWorkflowId: updatedWorkflow?.id,
      updatedWorkflowName: updatedWorkflow?.name,
      phasesCount: updatedWorkflow?.phases?.length,
      nodesCount: updatedWorkflow?.nodes?.length,
      connectionsCount: updatedWorkflow?.connections?.length,
      hasOnWorkflowChange: !!onWorkflowChange
    });
    
    if (onWorkflowChange) {
      console.log('🔄 WORKFLOW-DEBUG: Calling onWorkflowChange with replace_workflow');
      onWorkflowChange({
        type: 'replace_workflow',
        workflow: updatedWorkflow
      });

      // Refresh validation against the incoming workflow data
      const validation = validateWorkflow(updatedWorkflow);
      setWorkflowValidation(validation);
      console.log('🔍 VALIDATION-REFRESH: Validation updated after workflow mutation', {
        isValid: validation.isValid,
        errorCount: validation.errors.length,
        warningCount: validation.warnings.length
      });

      // Reset the workflow fingerprint so the useEffect reprocesses the new structure
      lastProcessedWorkflowRef.current = '';

      // Signal the post-mutation layout hook — it will fire once React has
      // flushed the replace_workflow state update through to nodes
      triggerLayout();
      console.log('🔔 LAYOUT-TRIGGER: triggerLayout called after mutation');
    } else {
      console.warn('🔄 WORKFLOW-DEBUG: onWorkflowChange is not available!');
    }
  }, [onWorkflowChange, triggerLayout]);

  // State for workflow metadata - MUST be declared before saveWorkflow function
  const [workflowName, setWorkflowName] = useState(() => {
    const initialName = workflowData?.name || `New Workflow - ${new Date().toLocaleDateString()}`;
    console.log('🏗️ WORKFLOW-NAME-DEBUG: Initial workflowName state:', initialName);
    console.log('🏗️ WORKFLOW-NAME-DEBUG: workflowData:', workflowData);
    return initialName;
  });
  const [workflowDescription, setWorkflowDescription] = useState(workflowData?.description || '');

  // Add refs to track if user has manually changed the name or description
  const userHasChangedName = useRef(false);
  const userHasChangedDescription = useRef(false);

  // Add the missing isSaving state
  const [isSaving, setIsSaving] = useState(false);

  // Local fallback for the saved workflow id. Once we've saved this canvas
  // at least once, we MUST send the id on every subsequent save so the
  // backend updates instead of creating a new workflow. Previously we
  // relied solely on the parent's `workflowData` prop for this — when the
  // parent didn't echo the id back into its state after our onSave callback,
  // every re-save created a duplicate. This local state is a defensive
  // backstop that survives parent-state quirks.
  const [localSavedId, setLocalSavedId] = useState<string | undefined>(undefined);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getAutoSavePreference = (workflowId?: string) => {
    if (!workflowId) return true;
    const stored = localStorage.getItem(`workflow_autosave_${workflowId}`);
    return stored === null ? true : stored === 'true';
  };
  const [autoSave, setAutoSave] = useState(() => getAutoSavePreference(workflowData?.id));

  const handleAutoSaveToggle = (enabled: boolean) => {
    setAutoSave(enabled);
    if (workflowData?.id) {
      localStorage.setItem(`workflow_autosave_${workflowData.id}`, String(enabled));
    }
  };

  // Add tinted background effect for Quick Start panel
  const quickStartTintedBg = useTintedBackground({
    tintColor: darkMode ? '#10b981' : '#059669',
    enabled: true
  });

  // Handle test workflow button click
  const handleTestWorkflow = useCallback(async () => {
    if (!workflowData?.id) {
      workflowNotifications.workflowValidationError('Please save the workflow first before testing');
      return;
    }

    try {
      const sessions = await sessionService.listSessions();
      setAvailableSessions(sessions);
    } catch (err) {
      console.error("Failed to fetch sessions", err);
    }
    
    setShowTestModal(true);
  }, [workflowData?.id, workflowNotifications]);

  // Handle starting test execution
  const handleStartTestExecution = useCallback(async () => {
    if (!workflowData?.id) return;
    
    setIsCreatingSession(true);
    
    try {
      let sessionIdToUse = selectedSessionId;
      
      if (selectedSessionId === 'auto-create' || !selectedSessionId) {
        const timestamp = new Date().toLocaleString().replace(/[/,:]/g, '-');
        const randomSuffix = Math.floor(Math.random() * 10000);
        const sessionName = `Test: ${workflowName} (${timestamp}-${randomSuffix})`;
        
        const sessionResponse = await sessionService.createSession(sessionName);
        sessionIdToUse = sessionResponse.id || sessionResponse.session_id;
        
        if (!sessionIdToUse) {
          throw new Error('Failed to create session');
        }
      }
      
      const result = await workflowExecutionService.startWorkflowExecution(
        workflowData.id,
        sessionIdToUse
      );
      
      workflowNotifications.workflowExecutionStarted(workflowName);
      navigate(`/workflows/executions/${result.id}`);
    } catch (error) {
      console.error('Failed to start test execution:', error);
      workflowNotifications.workflowExecutionError(error.message || 'Unknown error');
    } finally {
      setIsCreatingSession(false);
      setShowTestModal(false);
    }
  }, [workflowData?.id, workflowName, selectedSessionId, navigate, workflowNotifications]);


  // Custom onNodesChange handler to track position changes and history
  const onNodesChange = useCallback((changes) => {
    // Check if this is a significant change that should be recorded
    const significantChange = changes.some(change => 
      change.type === 'add' || change.type === 'remove' || 
      (change.type === 'position' && change.dragging === false)
    );

    // Apply changes
    onNodesChangeOriginal(changes);
    
    
    // Record state for undo/redo if significant change
    if (significantChange) {
      setTimeout(() => {
        pushState(nodes, edges, 'node_change');
      }, 100);
    }
    
    // If this is a position change, notify about it
    changes.forEach(change => {
      if (change.type === 'position' && change.position && onWorkflowChange) {
        const node = nodes.find(n => n.id === change.id);
        if (node && node.type === 'phase') {
          onWorkflowChange({
            type: 'update_phase',
            phaseId: change.id,
            updates: { position: { ...change.position } }
          });
        }
      }
    });
  }, [nodes, edges, onNodesChangeOriginal, onWorkflowChange, pushState]);

  // Save workflow (edit mode) - MUST be declared before keyboard shortcuts useEffect
  const saveWorkflow = useCallback(async () => {
    console.log('💾 WORKFLOW-SAVE-DEBUG: ========== SAVE WORKFLOW STARTED ==========');
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflowName state:', workflowName);
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflowName type:', typeof workflowName);
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflowName length:', workflowName?.length);
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflowName.trim():', workflowName?.trim());
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflowDescription:', workflowDescription);

    if (!workflowName.trim()) {
      console.log('❌ WORKFLOW-SAVE-DEBUG: Name validation FAILED - empty name');
      console.log('❌ WORKFLOW-SAVE-DEBUG: workflowName raw:', JSON.stringify(workflowName));
      workflowNotifications.workflowValidationError('Please enter a workflow name');
      logWorkflowEvent('warning', 'Workflow save failed: No name provided');
      return;
    }

    console.log('✅ WORKFLOW-SAVE-DEBUG: Name validation PASSED');

    if (nodes.length === 0) {
      console.log('❌ WORKFLOW-SAVE-DEBUG: Nodes validation FAILED - no phases');
      workflowNotifications.workflowValidationError('Please add at least one phase to the workflow');
      logWorkflowEvent('warning', 'Workflow save failed: No phases added');
      return;
    }

    console.log('✅ WORKFLOW-SAVE-DEBUG: Nodes validation PASSED - nodes count:', nodes.length);

    // Log the workflow name being used
    const trimmedName = workflowName.trim();
    console.log('💾 WORKFLOW-SAVE-DEBUG: Final trimmed name to save:', trimmedName);
    console.log('💾 WORKFLOW-SAVE-DEBUG: Final trimmed name length:', trimmedName.length);
    console.log(`💾 WORKFLOW-SAVE-DEBUG: Saving workflow with name: "${trimmedName}"`);
    logWorkflowEvent('info', `Saving workflow: "${trimmedName}"`, {
      phases: nodes.filter(node => node.type === 'phase').length,
      tools: nodes.filter(node => node.type === 'tool').length,
      personas: nodes.filter(node => node.type === 'persona').length,
      artifacts: nodes.filter(node => node.type === 'artifact').length,
    });
    
    // Ensure all positions are valid before saving
    const phaseNodes = nodes.filter(node => node.type === 'phase');
    const invalidPositions = phaseNodes.filter(node => 
      !node.position || 
      typeof node.position.x !== 'number' || 
      typeof node.position.y !== 'number'
    );
    
    if (invalidPositions.length > 0) {
      // Fix invalid positions
      for (const node of invalidPositions) {
        node.position = { 
          x: 100 + Math.floor(Math.random() * 500), 
          y: 100 + Math.floor(Math.random() * 300) 
        };
      }
    }

    // Separate nodes by type (reuse the phaseNodes variable)
    const toolNodes = nodes.filter(node => node.type === 'tool');
    const personaNodes = nodes.filter(node => node.type === 'persona');
    const artifactNodes = nodes.filter(node => node.type === 'artifact');

    const workflow = {
      name: trimmedName, // Use the trimmed name variable for clarity
      description: workflowDescription,
      // Save phase nodes
      phases: phaseNodes.map(node => {
        return {
          id: node.id,
          type: node.data.type,
          label: node.data.label,
          position: {
            x: Number(node.position.x),
            y: Number(node.position.y)
          },
          config: node.data.config
        };
      }),
      // Save all other node types
      nodes: [
        ...toolNodes.map(node => {
          return {
            id: node.id,
            type: 'tool',
            position: {
              x: Number(node.position.x),
              y: Number(node.position.y)
            },
            data: {
              toolId: node.data.toolId,
              toolName: node.data.toolName,
              toolCategory: node.data.toolCategory,
              toolDescription: node.data.toolDescription,
              toolVersion: node.data.toolVersion
            }
          };
        }),
        ...personaNodes.map(node => {
          return {
            id: node.id,
            type: 'persona',
            position: {
              x: Number(node.position.x),
              y: Number(node.position.y)
            },
            data: {
              personaId: node.data.personaId,
              personaName: node.data.personaName,
              personaSpecialization: node.data.personaSpecialization,
              personaDescription: node.data.personaDescription
            }
          };
        }),
        ...artifactNodes.map(node => {
          return {
            id: node.id,
            type: 'artifact',
            position: {
              x: Number(node.position.x),
              y: Number(node.position.y)
            },
            data: {
              isOutput: node.data.isOutput,
              selectedArtifacts: node.data.selectedArtifacts || []
            }
          };
        })
      ],
      connections: edges.map(edge => {
        // Log the edge being saved
        console.log(`Saving edge ${edge.id} with sourceHandle=${edge.sourceHandle}, targetHandle=${edge.targetHandle}`);
        
        return {
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,  // Use exact handle ID
          targetHandle: edge.targetHandle,  // Use exact handle ID
          dependencyType: edge.data?.type || 'default'
        };
      })
    };

    console.log('💾 WORKFLOW-SAVE-DEBUG: Complete workflow object being saved:');
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflow.name:', workflow.name);
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflow.description:', workflow.description);
    console.log('💾 WORKFLOW-SAVE-DEBUG: workflow.phases.length:', workflow.phases?.length);
    console.log('💾 WORKFLOW-SAVE-DEBUG: Full workflow object:', JSON.stringify(workflow, null, 2));

    // Check if we're updating an existing workflow or creating a new one.
    // Precedence: workflowData.id (from parent) > localSavedId (set by our
    // own previous save). The localSavedId fallback prevents the duplicate-
    // on-save bug when the parent doesn't echo the new id back into its
    // workflowData state after onSave.
    const effectiveId = (workflowData && workflowData.id) || localSavedId;
    const isUpdate = !!effectiveId;
    if (isUpdate) {
      workflow.id = effectiveId;
      console.log('💾 WORKFLOW-SAVE-DEBUG: UPDATING existing workflow:', effectiveId);
      logWorkflowEvent('info', `Updating existing workflow: ${effectiveId}`);
    } else {
      console.log('💾 WORKFLOW-SAVE-DEBUG: CREATING new workflow');
      logWorkflowEvent('info', 'Creating new workflow');
    }

    console.log('💾 WORKFLOW-SAVE-DEBUG: Saving workflow with connections:', workflow.connections);
    console.log("💾 WORKFLOW-SAVE-DEBUG: 📍 FULL WORKFLOW BEING SENT TO SERVER:", JSON.stringify(workflow, null, 2));

    try {
      setIsSaving(true);
      console.log('💾 WORKFLOW-SAVE-DEBUG: About to call sessionService.createWorkflow');
      console.log('💾 WORKFLOW-SAVE-DEBUG: Workflow object being sent to API:', workflow);
      
      // Log current node positions before saving
      console.log("📍 CURRENT NODE POSITIONS BEFORE SAVE:");
      nodes.forEach(node => {
        if (node.type !== 'phase') {
          console.log(`Node ${node.id} position:`, node.position);
        }
      });
    
      // Save the workflow and wait for the response
      const response = await sessionService.createWorkflow(workflow);
      console.log('💾 WORKFLOW-SAVE-DEBUG: API response received:', response);
      console.log('💾 WORKFLOW-SAVE-DEBUG: Response type:', typeof response);
      console.log('💾 WORKFLOW-SAVE-DEBUG: Response.id:', response?.id);
      console.log('💾 WORKFLOW-SAVE-DEBUG: Response.name:', response?.name);
      console.log('Workflow saved successfully:', response);
  
      logWorkflowEvent('success', `Workflow saved successfully: ${response.id}`, response);
      workflowNotifications.workflowSaved(workflowName);
  
      // Remember the saved id locally so the NEXT save updates instead of
      // creating a duplicate, even if the parent doesn't echo it back into
      // workflowData.
      if (response?.id) {
        setLocalSavedId(response.id);
      }

      // Create a complete workflow object with the ID from the response
      const savedWorkflow = {
        ...workflow,  // Keep all the original workflow data (name, description, phases, nodes, connections)
        id: response.id,  // Add the ID from the response
        created_at: response.created_at,
        updated_at: response.updated_at
      };

      console.log('💾 WORKFLOW-SAVE-DEBUG: Created complete savedWorkflow with ID:', savedWorkflow.id);
      console.log('💾 WORKFLOW-SAVE-DEBUG: savedWorkflow.name:', savedWorkflow.name);
      console.log('💾 WORKFLOW-SAVE-DEBUG: savedWorkflow has phases:', savedWorkflow.phases?.length);
      console.log('💾 WORKFLOW-SAVE-DEBUG: savedWorkflow has nodes:', savedWorkflow.nodes?.length);
      console.log('💾 WORKFLOW-SAVE-DEBUG: savedWorkflow has connections:', savedWorkflow.connections?.length);

      if (onSave) {
        console.log('💾 WORKFLOW-SAVE-DEBUG: Calling onSave with complete savedWorkflow');
        onSave(savedWorkflow);  // Pass the complete workflow with ID
      }
    } catch (error) {
      console.error('💾 WORKFLOW-SAVE-DEBUG: API call FAILED:', error);
      console.error('💾 WORKFLOW-SAVE-DEBUG: Error details:', error.message);
      console.error('💾 WORKFLOW-SAVE-DEBUG: Error response:', error.response?.data);
      console.error('Error saving workflow:', error);
      logWorkflowEvent('error', 'Failed to save workflow', error);
      workflowNotifications.workflowSaveError(error.message || 'Unknown error');
    } finally {
      setIsSaving(false);
    }
  }, [workflowName, workflowDescription, nodes, edges, onSave, workflowData, workflowNotifications]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'z':
            event.preventDefault();
            if (event.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'y':
            event.preventDefault();
            redo();
            break;
          case 's':
            if (mode === WorkflowCanvasMode.EDIT) {
              event.preventDefault();
              saveWorkflow();
            }
            break;
          case 'a':
            event.preventDefault();
            // Select all nodes and edges
            const nodeChanges = nodes.map(node => ({
              id: node.id,
              type: 'select' as const,
              selected: true
            }));
            
            const edgeChanges = edges.map(edge => ({
              id: edge.id,
              type: 'select' as const,
              selected: true
            }));
            
            onNodesChange(nodeChanges);
            onEdgesChange(edgeChanges);
            break;
          case 'c':
            event.preventDefault();
            // TODO: Implement copy functionality
            console.log('Copy selected items');
            break;
          case 'v':
            event.preventDefault();
            // TODO: Implement paste functionality
            console.log('Paste items');
            break;
        }
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        // Only handle delete/backspace if not typing in an input field
        const activeElement = document.activeElement;
        const isTypingInInput = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.contentEditable === 'true'
        );
        
        if (!isTypingInInput) {
          // Delete selected items
          event.preventDefault();
          // TODO: Implement delete selected functionality
          console.log('Delete selected items');
        }
      } else if (event.key === 'Escape') {
        // Clear selection
        event.preventDefault();
        bulkOperations.clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, saveWorkflow, mode, nodes, edges, onNodesChange, onEdgesChange]);

  // State for selected node
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  // Remove separate selection state - ReactFlow handles this
  const [isBoxSelecting, setIsBoxSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
  } | null>(null);

  // Connection preview state
  const [connectionPreview, setConnectionPreview] = useState<{
    sourceId: string;
    sourceHandle: string;
    validTargets: string[];
  } | null>(null);

  // Template selector state
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  // State for view options
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [viewMode, setViewMode] = useState<'default' | 'timeline' | 'parallel'>('default');
  const [showMetrics, setShowMetrics] = useState(false);
  const [connectorType, setConnectorType] = useState<'curved' | 'straight' | 'smoothstep' | 'step'>('curved');

  // Workflow validation - ensure it updates when workflow data changes
  const [workflowValidation, setWorkflowValidation] = useState(null);
  
  // Update validation whenever workflow data changes
  useEffect(() => {
    if (workflowData) {
      const validation = validateWorkflow(workflowData);
      setWorkflowValidation(validation);
      console.log('🔍 VALIDATION-UPDATE: Validation refreshed', {
        isValid: validation.isValid,
        errorCount: validation.errors.length,
        warningCount: validation.warnings.length,
        workflowDataId: workflowData.id,
        phasesCount: workflowData.phases?.length,
        nodesCount: workflowData.nodes?.length
      });
    } else {
      setWorkflowValidation(null);
    }
  }, [workflowData, nodes, edges]); // Re-validate when workflow data or nodes/edges change


  // State for confirmation modals
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);
  const [showDeleteEdgeConfirmation, setShowDeleteEdgeConfirmation] = useState(false);
  const [edgeToDelete, setEdgeToDelete] = useState<string | null>(null);

  // Update workflow metadata when data changes
  useEffect(() => {
    console.log('🔄 WORKFLOW-NAME-DEBUG: useEffect triggered');
    console.log('🔄 WORKFLOW-NAME-DEBUG: workflowData:', workflowData);
    console.log('🔄 WORKFLOW-NAME-DEBUG: Current workflowName state:', workflowName);
    
    if (workflowData) {
      console.log('🔄 WORKFLOW-NAME-DEBUG: Setting name from workflowData:', workflowData.name);
      // Only update from workflowData if user hasn't manually changed the name
      if (!userHasChangedName.current) {
        setWorkflowName(workflowData.name || '');
      }
      // Only update description if user hasn't manually changed it
      if (!userHasChangedDescription.current) {
        setWorkflowDescription(workflowData.description || '');
      }
    } else {
      const defaultName = `New Workflow - ${new Date().toLocaleDateString()}`;
      console.log('🔄 WORKFLOW-NAME-DEBUG: No workflowData, setting default name:', defaultName);
      console.log('🔄 WORKFLOW-NAME-DEBUG: Current workflowName before default:', workflowName);
      
      // Only set default name if current name is empty AND user hasn't changed it
      if (!workflowName && !userHasChangedName.current) {
        console.log('🔄 WORKFLOW-NAME-DEBUG: Setting default name because current is empty');
        setWorkflowName(defaultName);
      } else {
        console.log('🔄 WORKFLOW-NAME-DEBUG: Keeping existing name:', workflowName);
      }
      
      // Don't reset description to empty if user has changed it
      if (!userHasChangedDescription.current && !workflowDescription) {
        setWorkflowDescription('');
      }
    }
  }, [workflowData]);

  // Reference to track if this is the initial render
  const initialRenderRef = useRef(true);

  // Track the last processed workflow fingerprint to avoid reprocessing on node position changes
  const lastProcessedWorkflowRef = useRef<string>('');

  // Transform workflow data into nodes and edges
  useEffect(() => {
    console.log('🔄 WORKFLOW-UPDATE-TRIGGER: useEffect triggered by:', {
      workflowDataChanged: !!workflowData,
      executionDataChanged: !!executionData,
      modeChanged: mode,
      nodesLength: nodes.length
    });

    if (!workflowData) return;

    // Compute a fingerprint of the workflow structure (not positions)
    // Only reprocess if the workflow identity has actually changed
    const workflowFingerprint = [
      workflowData.id ?? 'no-id',
      workflowData.phases?.length ?? 0,
      workflowData.nodes?.length ?? 0,
      workflowData.connections?.length ?? 0,
      workflowData.updated_at ?? '',
    ].join('|');

    const isStructuralChange = workflowFingerprint !== lastProcessedWorkflowRef.current;

    // If nodes exist and workflow structure hasn't changed, skip reprocessing
    // This prevents dagre layout positions from being overwritten by stale workflowData
    if (nodes.length > 0 && !isStructuralChange) {
      console.log('🔄 WORKFLOW-EFFECT: Skipping reprocess — workflow structure unchanged, nodes already positioned');
      return;
    }

    // Always update the fingerprint before any early returns below
    lastProcessedWorkflowRef.current = workflowFingerprint;

    // Set up global debug callback for recovery scenarios
    window.workflowDebugCallback = (type: string, nodeId: string, updates: any) => {
      console.log('🔧 GLOBAL-DEBUG-CALLBACK: Called with:', { type, nodeId, updates });
      if (onWorkflowChange) {
        onWorkflowChange({
          type,
          personaNodeId: nodeId,
          updates
        });
      }
    };

    const fixedWorkflowData = workflowData;
    
    // Use the validateAndNormalizePosition function imported at the top of the file
    const { validateAndNormalizePosition } = workflowDataProcessor;

    console.log(`🔄 WORKFLOW CANVAS: Processing workflow data for mode ${mode}`);
    
    // Log the positions in the workflow data with validation
    if (fixedWorkflowData.phases) {
      console.log('📍 POSITIONS IN WORKFLOW DATA BEFORE PROCESSING:');
      fixedWorkflowData.phases.forEach(phase => {
        logNodePosition('WORKFLOW_DATA', phase.id, phase.position);
        validatePosition(phase.position, phase.id, 'WORKFLOW_DATA');
      });
      
      // Check if this is an older workflow by looking at created_at
      const isOlderWorkflow = fixedWorkflowData.created_at && 
        new Date(fixedWorkflowData.created_at).getTime() < (Date.now() - 24 * 60 * 60 * 1000);
      
      console.log(`📊 IS OLDER WORKFLOW: ${isOlderWorkflow}, created_at: ${fixedWorkflowData.created_at}`);
      
      // Log detailed position information for each phase
      fixedWorkflowData.phases.forEach(phase => {
        console.log(`📊 PHASE ${phase.id} POSITION DETAILS:`, {
          hasPosition: !!phase.position,
          positionType: phase.position ? typeof phase.position : 'undefined',
          xType: phase.position?.x ? typeof phase.position.x : 'undefined',
          yType: phase.position?.y ? typeof phase.position.y : 'undefined',
          xValue: phase.position?.x,
          yValue: phase.position?.y
        });
      });
    }
    
    // Log entity node positions if they exist
    if (fixedWorkflowData.nodes) {
      console.log('📍 ENTITY NODES IN WORKFLOW DATA:');
      fixedWorkflowData.nodes.forEach(node => {
        logNodePosition('WORKFLOW_DATA_ENTITY', node.id, node.position);
        validatePosition(node.position, node.id, 'WORKFLOW_DATA_ENTITY');
      });
    }
    
    // Check if we have a complete workflow data structure
    if (!fixedWorkflowData.phases || !fixedWorkflowData.connections) {
      console.warn('WorkflowCanvas: Incomplete workflow data received, missing phases or connections:', fixedWorkflowData);
      return; // Exit early if we don't have the necessary data
    }

    // Don't recreate nodes if we already have them - just update them
    if (nodes.length > 0) {
      console.log('WorkflowCanvas: Updating existing nodes instead of recreating them');
      
      console.log('🔄 WORKFLOW-UPDATE-CALLING: About to call updateNodesWithWorkflowData');
      console.log('🔄 WORKFLOW-UPDATE-CALLING: Current nodes have callbacks:', 
        nodes.map(n => ({ id: n.id, hasOnUpdate: !!n.data?.onUpdate }))
      );
      
      // CRITICAL: Preserve callbacks before any processing
      const nodesWithPreservedCallbacks = nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          // Ensure callbacks are preserved
          onUpdate: node.data?.onUpdate,
          onToggleExpand: node.data?.onToggleExpand
        }
      }));
      
      console.log('🔄 WORKFLOW-CALLBACK-PRESERVE: Preserved callbacks for nodes:', 
        nodesWithPreservedCallbacks.map(n => ({ id: n.id, hasOnUpdate: !!n.data?.onUpdate }))
      );
      
      // Update nodes with new workflow data
      const updatedNodes = updateNodesWithWorkflowData(
        nodesWithPreservedCallbacks, // Use nodes with preserved callbacks
        fixedWorkflowData,
        executionData,
        mode,
        darkMode,
        onWorkflowChange,
        (nodeId, isExpanded) => {
          setNodes(nodes => nodes.map(node => 
            node.id === nodeId ? { ...node, data: { ...node.data, expanded: isExpanded } } : node
          ));
        },
        true // Always preserve positions during active editing
      );
      
      console.log('🔄 WORKFLOW-CALLBACK-FINAL: Final nodes have callbacks:', 
        updatedNodes.map(n => ({ id: n.id, hasOnUpdate: !!n.data?.onUpdate }))
      );
      
      // Update edges based on connections
      const updatedEdges = createEdgesFromConnections(
        fixedWorkflowData.connections,
        updatedNodes,
        showCriticalPath ? calculateCriticalPath(fixedWorkflowData) : []
      );
      
      // FORCE IMMEDIATE UPDATE
      setNodes(updatedNodes);
      setEdges(updatedEdges);

      // Force React to flush the update immediately
      setTimeout(() => {
        console.log('🔄 FORCED-UPDATE: Nodes updated immediately');
      }, 0);
      
      return;
    }
    
    // If we don't have nodes yet, create them from scratch
    console.log('WorkflowCanvas: Creating nodes from scratch');
    
    // Process workflow data into nodes and edges
    const { nodes: processedNodes, edges: processedEdges } = processWorkflowData(
      fixedWorkflowData,
      executionData,
      mode,
      darkMode,
      onWorkflowChange,
      (nodeId, isExpanded) => {
        setNodes(nodes => nodes.map(node => 
          node.id === nodeId ? { ...node, data: { ...node.data, expanded: isExpanded } } : node
        ));
      }
    );

    setNodes(processedNodes);
    setEdges(processedEdges);

    // Only apply layout and fitView on initial render if positions aren't already set
    if (initialRenderRef.current) {
      setTimeout(() => {
        // Check if nodes already have valid positions from the data
        const nodesHavePositions = processedNodes.every(node => 
          node.position && 
          typeof node.position.x === 'number' && 
          typeof node.position.y === 'number'
        );
          
        if (!nodesHavePositions) {
          console.log("Applying initial layout because nodes don't have valid positions");
          applyLayout(processedNodes, setNodes, viewMode, fixedWorkflowData, fitView);
        } else {
          console.log("Skipping initial layout because nodes already have valid positions");
        }
          
        fitView({ padding: 0.2 });
        initialRenderRef.current = false;
      }, 100);
    }
  }, [workflowData, executionData, mode, showCriticalPath, darkMode, onWorkflowChange, setNodes, setEdges]);

  // Apply layout when view mode changes (skip 'default' — positions come from data)
  useEffect(() => {
    if (viewMode === 'default') return;
    applyLayout(nodes, setNodes, viewMode, workflowData, fitView);
  }, [viewMode]);

  // Debounced auto-save
  useEffect(() => {
    if (mode !== WorkflowCanvasMode.EDIT) return;
    if (!autoSave) return;
    if (nodes.filter(n => n.type === 'phase').length === 0) return;

    setSaveStatus('unsaved');
    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(async () => {
      setSaveStatus('saving');
      const name = (!workflowName.trim() || workflowName.startsWith('New Workflow'))
        ? (workflowData?.name?.trim() && !workflowData.name.startsWith('New Workflow'))
          ? workflowData.name
          : `Untitled Workflow - ${new Date().toLocaleDateString()}`
        : workflowName.trim();
      await saveWorkflow(name);
      setSaveStatus('saved');
    }, 2000);

    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [nodes, edges, workflowName, workflowDescription, autoSave, mode]);

  // Apply undo/redo state changes
  useEffect(() => {
    if (currentState && currentState.nodes && currentState.edges) {
      setNodes(currentState.nodes);
      setEdges(currentState.edges);
    }
  }, [currentState, setNodes, setEdges]);

  // Get ReactFlow instance
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  
  // Add node drag stop handler
  const onNodeDragStop = useCallback((event, node) => {
    logNodePosition('NODE_DRAG_STOP', node.id, node.position);
    validatePosition(node.position, node.id, 'NODE_DRAG_STOP');
    
    console.log(`📍 WORKFLOW NODE[${node.id}] DRAG STOPPED: type=${node.type}`);
    
    // Update the workflow data with the new position
    if (onWorkflowChange) {
      if (node.type === 'phase') {
        onWorkflowChange({
          type: 'update_phase',
          phaseId: node.id,
          updates: { position: { ...node.position } }
        });
      } else if (node.type === 'tool') {
        onWorkflowChange({
          type: 'update_tool',
          toolNodeId: node.id,
          updates: { position: { ...node.position } }
        });
      } else if (node.type === 'persona') {
        onWorkflowChange({
          type: 'update_persona',
          personaNodeId: node.id,
          updates: { position: { ...node.position } }
        });
      } else if (node.type === 'artifact') {
        onWorkflowChange({
          type: 'update_artifact',
          artifactNodeId: node.id,
          updates: { position: { ...node.position } }
        });
      }
    }
  }, [onWorkflowChange]);

  // Strip layout transition the instant the user starts dragging — keeps drag feeling instant
  const onNodeDragStart = useCallback(() => {
    if (isLayoutPending()) return; // still animating, don't interfere
    setNodes(nds => nds.map(n => ({
      ...n,
      style: { ...n.style, transition: undefined },
    })));
  }, [setNodes, isLayoutPending]);

  // Use the extracted workflow operations hook
  const workflowOperations = useWorkflowOperations(
    nodes,
    setNodes,
    edges,
    setEdges,
    mode,
    darkMode,
    onWorkflowChange,
    readOnly
  );

  // Confirmation handlers
  const handleConfirmDelete = useCallback(() => {
    if (nodeToDelete) {
      workflowOperations.deleteNode(nodeToDelete);
      setNodeToDelete(null);
    }
    setShowDeleteConfirmation(false);
  }, [nodeToDelete, workflowOperations]);

  const handleCancelDelete = useCallback(() => {
    setNodeToDelete(null);
    setShowDeleteConfirmation(false);
  }, []);

  const handleConfirmDeleteEdge = useCallback(() => {
    if (edgeToDelete) {
      workflowOperations.deleteEdge(edgeToDelete);
      setEdgeToDelete(null);
    }
    setShowDeleteEdgeConfirmation(false);
  }, [edgeToDelete, workflowOperations]);

  const handleCancelDeleteEdge = useCallback(() => {
    setEdgeToDelete(null);
    setShowDeleteEdgeConfirmation(false);
  }, []);

  // Create context menu handlers
  const contextMenuHandlers = {
    // Node handlers
    onViewNodeDetails: (id) => {
      console.log(`View details for ${id}`);
    },
    onShowDependencies: (id) => {
      console.log(`Show dependencies for ${id}`);
    },
    onToggleExpand: (id) => {
      setNodes(nodes => nodes.map(node => 
        node.id === id ? { ...node, data: { ...node.data, expanded: !node.data.expanded } } : node
      ));
    },
    onAddConnected: (id, direction) => {
      workflowOperations.addConnectedNode(id, direction);
    },
    onDeleteNode: (id) => {
      setNodeToDelete(id);
      setShowDeleteConfirmation(true);
    },
    onDuplicateNode: (id) => {
      console.log('Duplicate node');
    },
    onSetAsStart: (id) => {
      console.log('Set as start node');
    },
    onEditDetails: (id) => {
      console.log(`Edit details for ${id}`);
    },
    
    // Edge handlers
    onChangeDependencyType: (id, type) => {
      workflowOperations.changeDependencyType(id, type);
    },
    onReverseDirection: (id) => {
      console.log('Reverse edge direction');
    },
    onDeleteEdge: (id) => {
      setEdgeToDelete(id);
      setShowDeleteEdgeConfirmation(true);
    },
    
    // Canvas handlers
    onAddPhase: (type, position) => {
      workflowOperations.addNode(type, `New ${type} Phase`, position);
    },
    onArrangeLayout: () => {
      applyLayout(nodes, setNodes, viewMode, workflowData, fitView);
    },
    onFitView: () => {
      fitView({ padding: 0.2 });
    },
    onHighlightCriticalPath: () => {
      setShowCriticalPath(!showCriticalPath);
    },
    onChangeViewMode: (newMode) => {
      setViewMode(newMode);
    },
    
    // Execution handlers
    onStartPhase: (id) => {
      console.log(`Start phase ${id}`);
    },
    onCompletePhase: (id) => {
      console.log(`Complete phase ${id}`);
    },
    onSkipPhase: (id) => {
      console.log(`Skip phase ${id}`);
    },
    onViewArtifacts: (id) => {
      console.log(`View artifacts for phase ${id}`);
    },
    onPauseExecution: () => {
      console.log('Pause execution');
    },
    onResumeExecution: () => {
      console.log('Resume execution');
    },
    onViewExecutionLog: () => {
      console.log('View execution log');
    },
    onViewAllArtifacts: () => {
      console.log('View all artifacts');
    }
  };
  
  // Create context menu manager
  const contextMenuManager = new WorkflowContextMenuManager(
    mode,
    contextMenuHandlers,
    {
      executionData,
      currentViewMode: viewMode,
      phaseTypes
    }
  );
  
  // Close context menu
  const closeContextMenu = useCallback(() => {
    console.log(`Closing context menu`);
    setContextMenu(null);
  }, []);
  
  // Use the extracted context menu hook
  const {
    contextMenu,
    setContextMenu,
    onNodeContextMenu,
    onEdgeContextMenu,
    onPaneContextMenu: baseOnPaneContextMenu
  } = useWorkflowContextMenu(contextMenuManager, closeContextMenu);
  
  // Wrap the pane context menu to include reactFlowInstance and wrapper
  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
    baseOnPaneContextMenu(event, reactFlowInstance, reactFlowWrapper);
  }, [baseOnPaneContextMenu, reactFlowInstance, reactFlowWrapper]);

  // Simplified selection handler - just for logging/debugging
  const handleSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }) => {
    const nodeIds = selectedNodes.map(node => node.id);
    const edgeIds = selectedEdges.map(edge => edge.id);
    
    console.log('🔍 SELECTION CHANGE:', { nodeIds, edgeIds }); // Debug log
    
    // Don't manage separate state - ReactFlow is the source of truth
  }, []);

  // Bulk operations - work with ReactFlow's native selection
  const bulkOperations = {
    alignLeft: () => {
      const selectedNodeObjects = nodes.filter(node => node.selected);
      console.log('🔍 ALIGN LEFT:', selectedNodeObjects.length, 'nodes'); // Debug log
      
      if (selectedNodeObjects.length < 2) return;
      
      const leftMost = Math.min(...selectedNodeObjects.map(node => node.position.x));
      
      const nodeChanges = selectedNodeObjects.map(node => ({
        id: node.id,
        type: 'position' as const,
        position: { ...node.position, x: leftMost }
      }));
      
      onNodesChange(nodeChanges);
    },
    
    alignTop: () => {
      const selectedNodeObjects = nodes.filter(node => node.selected);
      console.log('🔍 ALIGN TOP:', selectedNodeObjects.length, 'nodes'); // Debug log
      
      if (selectedNodeObjects.length < 2) return;
      
      const topMost = Math.min(...selectedNodeObjects.map(node => node.position.y));
      
      const nodeChanges = selectedNodeObjects.map(node => ({
        id: node.id,
        type: 'position' as const,
        position: { ...node.position, y: topMost }
      }));
      
      onNodesChange(nodeChanges);
    },
    
    distributeHorizontally: () => {
      const selectedNodeObjects = nodes.filter(node => node.selected);
      if (selectedNodeObjects.length < 3) return;
      
      selectedNodeObjects.sort((a, b) => a.position.x - b.position.x);
      const leftMost = selectedNodeObjects[0].position.x;
      const rightMost = selectedNodeObjects[selectedNodeObjects.length - 1].position.x;
      const spacing = (rightMost - leftMost) / (selectedNodeObjects.length - 1);
      
      const nodeChanges = selectedNodeObjects.map((node, index) => {
        if (index === 0 || index === selectedNodeObjects.length - 1) {
          return null; // Don't move first and last
        }
        
        return {
          id: node.id,
          type: 'position' as const,
          position: { ...node.position, x: leftMost + (spacing * index) }
        };
      }).filter(Boolean);
      
      onNodesChange(nodeChanges);
    },
    
    distributeVertically: () => {
      const selectedNodeObjects = nodes.filter(node => node.selected);
      if (selectedNodeObjects.length < 3) return;
      
      selectedNodeObjects.sort((a, b) => a.position.y - b.position.y);
      const topMost = selectedNodeObjects[0].position.y;
      const bottomMost = selectedNodeObjects[selectedNodeObjects.length - 1].position.y;
      const spacing = (bottomMost - topMost) / (selectedNodeObjects.length - 1);
      
      const nodeChanges = selectedNodeObjects.map((node, index) => {
        if (index === 0 || index === selectedNodeObjects.length - 1) {
          return null; // Don't move first and last
        }
        
        return {
          id: node.id,
          type: 'position' as const,
          position: { ...node.position, y: topMost + (spacing * index) }
        };
      }).filter(Boolean);
      
      onNodesChange(nodeChanges);
    },
    
    deleteSelected: () => {
      const selectedNodeIds = nodes.filter(node => node.selected).map(node => node.id);
      selectedNodeIds.forEach(nodeId => {
        workflowOperations.deleteNode(nodeId);
      });
    },
    
    clearSelection: () => {
      console.log('🔍 CLEAR SELECTION called'); // Debug log
      
      const nodeChanges = nodes.map(node => ({
        id: node.id,
        type: 'select' as const,
        selected: false
      }));
      
      const edgeChanges = edges.map(edge => ({
        id: edge.id,
        type: 'select' as const,
        selected: false
      }));
      
      onNodesChange(nodeChanges);
      onEdgesChange(edgeChanges);
    }
  };

  // Handle node click - simplified
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    // Don't interfere with node interactions in edit mode
    if (mode === WorkflowCanvasMode.EDIT) {
      // Completely ignore persona node clicks - they handle their own interactions
      if (node.type === 'persona') {
        return;
      }
      
      // Only handle selection if user is holding Ctrl/Cmd for multi-select
      if (event.ctrlKey || event.metaKey) {
        onNodesChange([{
          id: node.id,
          type: 'select' as const,
          selected: !node.selected
        }]);
      }
      return;
    }

    // For other modes, handle as before
    setSelectedNode(node);

    if (mode === WorkflowCanvasMode.EXECUTE && onWorkflowChange) {
      onWorkflowChange({
        type: 'select_phase',
        phaseId: node.id,
        data: node.data
      });
    }
  }, [mode, onWorkflowChange, onNodesChange]);

  // Removed pane click handler - clicking canvas no longer clears selection
  // const onPaneClick = useCallback(() => {
  //   console.log('🔍 PANE CLICKED - clearing selection'); // Debug log
  //   bulkOperations.clearSelection();
  // }, [bulkOperations]);

  // Template application
  const applyTemplate = useCallback((templateKey: string) => {
    if (templateKey === 'blank') {
      setShowTemplateSelector(false);
      return;
    }

    const template = WorkflowTemplates[templateKey];
    if (!template) return;

    // Clear existing workflow
    setNodes([]);
    setEdges([]);

    // Apply template phases
    template.phases.forEach(phase => {
      const newNode = {
        id: phase.id,
        type: 'phase',
        position: phase.position,
        data: {
          id: phase.id,
          mode,
          label: phase.label,
          type: phase.type,
          config: phase.config,
          darkMode,
          onUpdate: (nodeId, updates) => {
            if (onWorkflowChange) {
              onWorkflowChange({
                type: 'update_phase',
                phaseId: nodeId,
                updates
              });
            }
          },
          onToggleExpand: (nodeId, isExpanded) => {
            setNodes(nodes => nodes.map(node => 
              node.id === nodeId ? { ...node, data: { ...node.data, expanded: isExpanded } } : node
            ));
          }
        }
      };

      if (onWorkflowChange) {
        onWorkflowChange({
          type: 'add_node',
          node: newNode
        });
      }
    });

    // Apply template connections
    template.connections.forEach(connection => {
      if (onWorkflowChange) {
        onWorkflowChange({
          type: 'add_connection',
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          dependencyType: connection.dependencyType
        });
      }
    });

    setShowTemplateSelector(false);
    
    // Fit view after template is applied
    setTimeout(() => {
      fitView({ padding: 0.2 });
    }, 100);
  }, [mode, darkMode, onWorkflowChange, setNodes, setEdges, fitView]);



  const canvasHeight = getCanvasHeight();

  // Calculate selected count from ReactFlow's native selection
  const selectedNodesCount = nodes.filter(node => node.selected).length;
  const selectedEdgesCount = edges.filter(edge => edge.selected).length;

  // Debug logging right before toolbar render
  console.log('🔍 TOOLBAR DEBUG:', {
    selectedNodesCount,
    selectedEdgesCount,
    nodesWithSelection: nodes.filter(n => n.selected).map(n => ({ id: n.id, selected: n.selected }))
  });

  return (
    <div style={{ height: canvasHeight }} className="flex flex-col">
      {/* Use the WorkflowToolbar component for edit mode */}
      {mode === WorkflowCanvasMode.EDIT && (
        <WorkflowToolbar
          workflowName={workflowName}
          workflowDescription={workflowDescription}
          onNameChange={(name) => {
            console.log('🔍 TOOLBAR-DEBUG: Name changed to:', JSON.stringify(name));
            console.log('🔍 TOOLBAR-DEBUG: Name type:', typeof name);
            console.log('📝 WORKFLOW-NAME-DEBUG: Toolbar onChange triggered');
            console.log('📝 WORKFLOW-NAME-DEBUG: Old value:', workflowName);
            console.log('📝 WORKFLOW-NAME-DEBUG: New value:', name);
            setWorkflowName(name);
            userHasChangedName.current = true;
          }}
          onDescriptionChange={(description) => {
            setWorkflowDescription(description);
            userHasChangedDescription.current = true;
          }}
          onSave={saveWorkflow}
          onBack={() => window.history.back()}
          onTest={handleTestWorkflow}
          canTest={!!workflowData?.id}
          isSaving={isSaving}
          isTesting={isCreatingSession}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showCriticalPath={showCriticalPath}
          onShowCriticalPathChange={setShowCriticalPath}
          showMetrics={showMetrics}
          onShowMetricsChange={setShowMetrics}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          workflowValidation={workflowValidation}
          layout="full"
          onClearSelection={bulkOperations.clearSelection}
          saveStatus={saveStatus}
          autoSave={autoSave}
          onAutoSaveToggle={handleAutoSaveToggle}
        />
      )}

      {/* Non-edit mode controls */}
      {mode !== WorkflowCanvasMode.EDIT && (
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">
              {mode === WorkflowCanvasMode.EXECUTE ? 'Workflow Execution' : 'Workflow Visualization'}
            </h3>
          </div>

          <div className="flex items-center space-x-4">
            <div>
              <label className="mr-2 text-sm">View Mode:</label>
              <select
                className={`border rounded px-2 py-1 ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as any)}
              >
                <option value="default">Default</option>
                <option value="timeline">Timeline</option>
                <option value="parallel">Parallel Execution</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="criticalPath"
                checked={showCriticalPath}
                onChange={(e) => setShowCriticalPath(e.target.checked)}
                className={`mr-1 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
              />
              <label htmlFor="criticalPath" className={`text-sm ${darkMode ? 'text-gray-300' : ''}`}>
                Show Critical Path
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ReactFlow canvas - takes remaining space */}
      <div
        ref={reactFlowWrapper}
        className={`flex-1 border rounded-lg overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
      >
        <BaseGraphCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={mode === WorkflowCanvasMode.EDIT && !readOnly ? onNodesChange : undefined}
          onEdgesChange={mode === WorkflowCanvasMode.EDIT && !readOnly ? onEdgesChange : undefined}
          onConnect={workflowOperations.onConnect}
          nodeTypes={workflowNodeTypes}
          edgeTypes={workflowEdgeTypes}
          onNodeClick={onNodeClick}
          onNodeContextMenu={onNodeContextMenu}
          onEdgeContextMenu={onEdgeContextMenu}
          onPaneContextMenu={onPaneContextMenu}
          onInit={setReactFlowInstance}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          onSelectionChange={handleSelectionChange}
          fitView
          // Enhanced zoom configuration for better workflow overview
          minZoom={0.1}        // Allow zooming out to 10% for large workflow overview
          maxZoom={4}          // Allow zooming in to 400% for detailed editing
          defaultZoom={1}      // Start at 100% zoom
          zoomOnScroll={true}  // Enable mouse wheel zoom
          zoomOnPinch={true}   // Enable pinch zoom on touch devices
          zoomOnDoubleClick={false} // Disable double-click zoom to avoid conflicts
          // New graph toolbar props
          showGraphToolbar={mode === WorkflowCanvasMode.EDIT}
          graphToolbarLayout="full"
          graphToolbarPosition="top"
          onLayoutChange={(layout) => {
            console.log('Layout changed to:', layout);
            // Apply the new layout
            applyLayout(nodes, setNodes, layout as any, workflowData, fitView);
          }}
          onConnectorTypeChange={(type) => {
            console.log('Connector type changed to:', type);
            setConnectorType(type as any);
            // Update all edges to use the new connector type
            setEdges(edges => edges.map(edge => ({
              ...edge,
              type: type === 'straight' ? 'straight' : 
                    type === 'curved' ? 'curved' :
                    type === 'smoothstep' ? 'smoothstep' :
                    type === 'step' ? 'step' : 'curved'
            })));
          }}
          onExportImage={() => {
            // TODO: Implement image export
            console.log('Export workflow as image');
          }}
          onExportData={() => {
            // TODO: Implement data export
            console.log('Export workflow data');
          }}
          // Bulk operations - NOW ON THE CORRECT TOOLBAR!
          onBulkAlignLeft={bulkOperations.alignLeft}
          onBulkAlignTop={bulkOperations.alignTop}
          onBulkDistributeHorizontally={bulkOperations.distributeHorizontally}
          onBulkDistributeVertically={bulkOperations.distributeVertically}
          onBulkDelete={bulkOperations.deleteSelected}
        >

          {/* Workflow Builder Panel (edit mode) */}
          {mode === WorkflowCanvasMode.EDIT && (
            <Panel position="top-left" data-testid="builder-panel">
              <div
                ref={quickStartTintedBg.ref}
                className={`p-2 rounded-xl shadow-xl max-w-xs border ${
                  darkMode 
                    ? 'border-gray-600/50' 
                    : 'border-gray-200/50'
                }`}
                style={{
                  ...quickStartTintedBg.style,
                  backgroundColor: darkMode ? 'rgba(31, 41, 55, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                }}
              >
                {/* Dynamic zoom overlay following cursor */}
                <div
                  className="absolute inset-0 pointer-events-none rounded-xl"
                  style={quickStartTintedBg.overlayStyle}
                />
                
                {/* Construction-themed BUILDER Header with CHAT button */}
                <div className={`mb-2 pb-2 border-b border-dashed ${
                  darkMode ? 'border-yellow-600/40' : 'border-yellow-500/40'
                }`}>
                  <div className="w-full flex justify-center items-center space-x-2">
                    {/* CHAT button with glow effect */}
                    {workflowData && (
                      <button
                        onClick={() => {
                          console.log('🗨️ CHAT-DEBUG: Chat toggle clicked, current state:', {
                            showChat,
                            workflowDataAvailable: !!workflowData,
                            workflowId: workflowData?.id,
                            mode
                          });
                          setShowChat(!showChat);
                        }}
                        className={`px-2 py-1 rounded font-bold text-xs tracking-wider transition-all duration-200 ${
                          showChat
                            ? darkMode 
                              ? 'bg-green-600/30 text-green-300 border border-green-600/50 shadow-[0_0_10px_rgba(34,197,94,0.5)] animate-pulse' 
                              : 'bg-green-100/80 text-green-700 border border-green-400/50 shadow-[0_0_8px_rgba(34,197,94,0.4)] animate-pulse'
                            : darkMode 
                              ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30 hover:bg-blue-600/30 hover:shadow-[0_0_8px_rgba(59,130,246,0.4)]' 
                              : 'bg-blue-100/60 text-blue-700 border border-blue-300/50 hover:bg-blue-100/80 hover:shadow-[0_0_6px_rgba(59,130,246,0.3)]'
                        }`}
                        title="Chat with Canvas"
                      >
                        💬 CHAT
                      </button>
                    )}
                    
                    {/* BUILDER badge */}
                    <div className={`px-3 py-1 rounded font-bold text-xs tracking-wider ${
                      darkMode 
                        ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/30' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                    }`}>
                      🚧 BUILDER
                    </div>
                  </div>
                </div>
                
                <div className="text-xs font-medium mb-1">Quick Start:</div>
              <div className="mb-2">
                <button
                  className="w-full px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 mb-1"
                  onClick={() => setShowTemplateSelector(true)}
                >
                  📋 Use Template
                </button>
              </div>
              
              <div className="text-xs font-medium mb-1">Add Phases:</div>
              <div className="grid grid-cols-2 gap-1 mb-2">
                {phaseTypes.map(phase => (
                  <button
                    key={phase.id}
                    className="px-1 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 truncate"
                    title={phase.label}
                    onClick={() => {
                      const centerX = window.innerWidth / 2;
                      const centerY = window.innerHeight / 2;
                      const position = reactFlowInstance.screenToFlowPosition({
                        x: centerX,
                        y: centerY
                      });
                      workflowOperations.addNode(phase.id, phase.label, position);
                    }}
                  >
                    {phase.label}
                  </button>
                ))}
              </div>
              
              <div className="text-xs font-medium mb-1">Add Entities:</div>
              <div className="grid grid-cols-2 gap-1">
                <button
                  className="px-1 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  onClick={() => {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;
                    const position = reactFlowInstance.screenToFlowPosition({
                      x: centerX,
                      y: centerY
                    });
                    workflowOperations.addToolNode(position);
                  }}
                >
                  Tool
                </button>
                
                <button
                  className="px-1 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  onClick={() => {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;
                    const position = reactFlowInstance.screenToFlowPosition({
                      x: centerX,
                      y: centerY
                    });
                    workflowOperations.addPersonaNode(position);
                  }}
                >
                  Persona
                </button>
                
                <button
                  className="px-1 py-0.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                  title="Input Artifacts"
                  onClick={() => {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;
                    const position = reactFlowInstance.screenToFlowPosition({
                      x: centerX,
                      y: centerY
                    });
                    workflowOperations.addArtifactNode(position, false);
                  }}
                >
                  Input
                </button>
                
                <button
                  className="px-1 py-0.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                  title="Output Artifacts"
                  onClick={() => {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;
                    const position = reactFlowInstance.screenToFlowPosition({
                      x: centerX,
                      y: centerY
                    });
                    workflowOperations.addArtifactNode(position, true);
                  }}
                >
                  Output
                </button>
              </div>
            </div>
            </Panel>
          )}


          {/* Layout refresh panel — edit mode only */}
          {mode === WorkflowCanvasMode.EDIT && (
            <Panel position="top-right">
              <div className={`p-1.5 rounded-lg shadow-lg border ${
                darkMode
                  ? 'bg-gray-900/90 border-green-800/50 backdrop-blur-sm'
                  : 'bg-white/90 border-green-200/50 backdrop-blur-sm'
              }`}>
                <LayoutRefreshButton
                  onRelayout={relayout}
                  onUndoLayout={undoLayout}
                  canUndoLayout={canUndoLayout}
                />
              </div>
            </Panel>
          )}

          {/* Unified Context Menu */}
          {contextMenu && (
            <UnifiedContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              items={contextMenu.items}
              onClose={closeContextMenu}
            />
          )}

          {/* Workflow Metrics Panel */}
          {workflowValidation && (
            <WorkflowMetricsPanel
              validation={workflowValidation}
              show={showMetrics}
            />
          )}

          {/* Real-time Validation Panel */}
          {workflowValidation && mode === WorkflowCanvasMode.EDIT && (
            <ValidationPanel
              validation={workflowValidation}
              show={true}
              mode={mode}
              onFocusNode={(nodeId) => {
                const node = nodes.find(n => n.id === nodeId);
                if (node && reactFlowInstance) {
                  reactFlowInstance.setCenter(node.position.x, node.position.y, { zoom: 1.2 });
                }
              }}
              onAutoFix={(issue) => {
                console.log('🔧 AUTO-FIX: Processing issue from ValidationPanel:', issue);
                
                // Use the fix instruction from ValidationPanel if available, otherwise generate one
                const fixInstruction = issue.fixInstruction || `Fix the validation issue: ${issue.message}`;
                
                console.log('🔧 AUTO-FIX: Using instruction:', fixInstruction);
                console.log('🔧 AUTO-FIX: Issue has personas:', !!issue.availablePersonas);
                console.log('🔧 AUTO-FIX: Issue has tools:', !!issue.availableTools);
                
                // Set the pending message and open chat
                setPendingChatMessage(fixInstruction);
                setShowChat(true);
                
                // Show a notification about the auto-fix
                console.log('Auto-fix: Opening chat with enhanced solution including available personas and tools');
              }}
            />
          )}

        </BaseGraphCanvas>
      </div>

      {/* Chat component - now as a fixed bottom-left panel */}
      {mode === WorkflowCanvasMode.EDIT && workflowData && (
        <div style={{ position: 'relative', zIndex: 1000 }}>
          <ChatWithCanvas
            workflowData={workflowData}
            onWorkflowUpdate={handleWorkflowUpdate}
            show={showChat}
            onToggle={() => setShowChat(!showChat)}
            pendingMessage={pendingChatMessage}
            onMessageSent={() => setPendingChatMessage('')}
          />
        </div>
      )}

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <TemplateSelector
          onSelectTemplate={applyTemplate}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {/* Delete Node Confirmation Modal */}
      {showDeleteConfirmation && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Node"
          message={`Are you sure you want to delete this ${nodeToDelete ? nodes.find(n => n.id === nodeToDelete)?.type || 'node' : 'node'}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      )}

      {/* Delete Edge Confirmation Modal */}
      {showDeleteEdgeConfirmation && (
        <ConfirmationModal
          isOpen={showDeleteEdgeConfirmation}
          onClose={handleCancelDeleteEdge}
          onConfirm={handleConfirmDeleteEdge}
          title="Delete Connection"
          message="Are you sure you want to delete this connection? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      )}

      {/* Test Workflow Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-xl max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <h3 className="text-lg font-bold mb-4">Test Workflow</h3>
            <p className="mb-4 text-sm opacity-80">
              Select a session to use for testing "{workflowName}":
            </p>
            
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Session
              </label>
              <select
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">Select a session...</option>
                <option value="auto-create">Auto-Create New Session</option>
                {availableSessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.prompt ? `${session.prompt.substring(0, 30)}...` : session.id}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowTestModal(false)}
                className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleStartTestExecution}
                disabled={isCreatingSession}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {isCreatingSession ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Starting...
                  </>
                ) : (
                  'Start Test'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowCanvas;
